import { useState } from 'react';
import { useMutation } from 'react-query';
import { Box, HStack, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Button, ControlInput, FormHelperText, Text } from '@pimm/base';
import { ChangePinRequest, PinResetDto } from '@pimm/services/lib/sms-tenants';
import { ChangePin } from '@pimm/services/lib/sms-tenants/services';
import { useFormPinSetup } from '../hooks';
import PinInput from './pin-input';

type FormPinSetupProps = {
  userId: string;
  onCancel?: () => void;
  onEndChange: (payload?: PinResetDto) => void;
};

export const FormPinSetup = ({ userId, ...props }: FormPinSetupProps) => {
  const [step, setStep] = useState<'enter-pin' | 'confirm-pin' | 'enter-password'>('enter-pin');
  const { t } = useTranslation();
  const form = useFormPinSetup();
  const errMessage = (form.formState.errors.pin?.message || form.formState.errors.root?.message) as string;

  const changePin = useMutation({
    mutationFn: ChangePin,
    onSuccess: data => props.onEndChange({ ...data, resultMessage: '0 day old' }),
    onError: (error: any) => {
      // Reset input fields but keep the error message
      form.reset(undefined, { keepErrors: true });
      // Return to the first step
      setStep('enter-pin');

      form.setError('root', {
        type: 'custom',
        message: (error.code ?? error.status) === 400 ? 'account_settings:invalid_password' : 'account_settings:error_try_again',
      });
    },
  });

  const handlePINChange = (_value: string) => {
    if (!_value) return;

    form.clearErrors();

    if (step === 'enter-pin') {
      form.setValue('pin', _value);
      setStep('confirm-pin');
      return;
    }

    if (step === 'confirm-pin') {
      const isValid = form.getValues().pin === _value;
      // Display error message if pin does not match
      if (!isValid) {
        form.setError('pin', { type: 'custom', message: t('account_settings:pin_does_not_match') });
      }
      setStep(isValid ? 'enter-password' : 'enter-pin');
    }
  };

  const handleSavePin = () => {
    const { currentPassword, pin } = form.getValues();
    const payload: ChangePinRequest = {
      userId: userId,
      currentPassword: btoa(currentPassword),
      newPin: pin,
    };
    changePin.mutate(payload);
  };

  return (
    <VStack height="full" w="full">
      <HStack alignItems="center" justifyContent="center" pt={4} px={4} h="50px">
        <Text size="2xl" fontWeight={700} color="gray.900" textAlign="center">
          {t('account_settings:pin_setup')}
        </Text>
      </HStack>

      <VStack space={2} justifyContent="center" pt={5} pb={4}>
        {step === 'enter-password' ? (
          <Box alignItems="center" justifyContent="center" px={6}>
            <Text size="md" fontWeight={600} color="gray.700" mb={2}>
              {t('account_settings:enter_password_to_continue')}
            </Text>
            <HStack space={2} alignItems="center">
              <ControlInput
                control={form.control}
                type="password"
                name="currentPassword"
                _helperText={{ textAlign: 'center' }}
                _input={{ w: 300 }}
                renderErrorText={_err => t(_err ?? '')}
              />
            </HStack>
          </Box>
        ) : (
          <Box alignItems="center" justifyContent="center" px={6}>
            <Text size="md" fontWeight={600} color="gray.700" mb={2}>
              {step === 'enter-pin' ? t('account_settings:enter_4_digit_pin') : t('account_settings:reenter_4_digit_pin')}
            </Text>
            <HStack space={2} alignItems="center">
              <PinInput key={step} onChange={handlePINChange} />
            </HStack>
          </Box>
        )}

        <FormHelperText error visible={!!errMessage} textAlign="center">
          {errMessage ? t(errMessage) : ''}
        </FormHelperText>
      </VStack>

      <HStack alignItems="center" justifyContent="flex-end" py={3} px={4} borderTopWidth={1}>
        <HStack space={3} justifyContent="flex-end">
          <Button variant="unstyled" minWidth={120} disabled={changePin.isLoading} onPress={props.onCancel}>
            {t('account_settings:cancel')}
          </Button>

          <Button minW={120} disabled={!form.formState.isValid} isLoading={changePin.isLoading} onPress={form.handleSubmit(handleSavePin)}>
            {t('account_settings:save')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};
